import { useModel } from '@umijs/max';
import { useCallback } from 'react';

/**
 * Model quản lý thông tin người dùng hiện tại (global store).
 *
 * Đồng bộ với @@initialState và cung cấp các helper
 * để truy cập / cập nhật currentUser dễ dàng hơn.
 *
 * Usage:
 *   const { currentUser, permissions, refreshCurrentUser } = useModel('currentUser');
 */
export default () => {
  type InitialStateShape = {
    currentUser?: API.UserData;
    permissions?: API.UserPermissionData[];
    fetchUserInfo?: () => Promise<
      | { currentUser: API.UserData; permissions: API.UserPermissionData[] }
      | undefined
    >;
  };

  const _initialModel = useModel('@@initialState') as {
    initialState?: InitialStateShape;
    setInitialState: (state: Partial<any> | ((prev: any) => any)) => void;
  };

  const { initialState, setInitialState } = _initialModel;

  const currentUser = initialState?.currentUser;
  const permissions = initialState?.permissions;

  /** Gọi lại API và cập nhật currentUser + permissions vào initialState */
  const refreshCurrentUser = useCallback(async () => {
    try {
      const fetchUserInfo = initialState?.fetchUserInfo;
      if (fetchUserInfo) {
        const res = await fetchUserInfo();
        if (res) {
          setInitialState((prev: any) => ({
            ...prev,
            currentUser: res.currentUser,
            permissions: res.permissions,
          }));
          return res.currentUser;
        }
      }
    } catch (error) {
      console.error('Failed to refresh current user:', error);
    }
    return undefined;
  }, [initialState?.fetchUserInfo, setInitialState]);

  /** Cập nhật trực tiếp currentUser vào store (không gọi API) */
  const setCurrentUser = useCallback(
    (user: API.UserData | undefined) => {
      setInitialState((prev: any) => ({
        ...prev,
        currentUser: user,
      }));
    },
    [setInitialState],
  );

  /** Cập nhật một phần thông tin currentUser */
  const updateCurrentUser = useCallback(
    (partial: Partial<API.UserData>) => {
      setInitialState((prev: any) => ({
        ...prev,
        currentUser: prev?.currentUser
          ? { ...prev.currentUser, ...partial }
          : undefined,
      }));
    },
    [setInitialState],
  );

  return {
    currentUser,
    permissions,
    refreshCurrentUser,
    setCurrentUser,
    updateCurrentUser,
  };
};
